import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import Cookies from "js-cookie";
import { queryClient } from "../App";
type Role = "super_admin" | "org_admin" | "user" | undefined;

type AuthContextType = {
  role?: Role;
  orgId?: string;
  orgName?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  updateAuth: (
    role?: Role,
    orgId?: string,
    orgName?: string,
    userId?: string,
    firstName?: string,
    lastName?: string,
    email?: string,
    remember?: boolean
  ) => void;
  clearAuth: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = useState<Role>(Cookies.get("role") as Role);
  const [orgId, setOrgId] = useState<string | undefined>(Cookies.get("org_id"));
  const [orgName, setOrgName] = useState<string | undefined>(Cookies.get("org_name"));
  const [userId, setUserId] = useState<string | undefined>(Cookies.get("user_id"));
  const [firstName, setFirstName] = useState<string | undefined>(Cookies.get("first_name"));
  const [lastName, setLastName] = useState<string | undefined>(Cookies.get("last_name"));
  const [email, setEmail] = useState<string | undefined>(Cookies.get("email"));

  // 🔄 Sync if cookies change outside React
  useEffect(() => {
    const timer = setInterval(() => {
      const r = Cookies.get("role") as Role;
      const o = Cookies.get("org_id");
      const n = Cookies.get("org_name");
      const i = Cookies.get("user_id");
      const f = Cookies.get("first_name");
      const l = Cookies.get("last_name");
      const e = Cookies.get("email");

      if (r !== role) setRole(r);
      if (o !== orgId) setOrgId(o);
      if (n !== orgName) setOrgName(n);
      if (i !== userId) setUserId(i);
      if (f !== firstName) setFirstName(f);
      if (l !== lastName) setLastName(l);
      if (e !== email) setEmail(e);
    }, 2000);
    return () => clearInterval(timer);
  }, [role, orgId, orgName, userId, firstName, lastName, email]);

  // 🔑 Update instantly
  const updateAuth = useCallback(
    (
      newRole?: Role,
      newOrgId?: string,
      newOrgName?: string,
      newUserId?: string,
      newFirstName?: string,
      newLastName?: string,
      newEmail?: string,
      remember = false
    ) => {
      const opts = remember ? { path: "/", expires: 30 } : { path: "/" };

      if (newRole) {
        Cookies.set("role", newRole, opts);
        setRole(newRole);
      }
      if (newOrgId) {
        Cookies.set("org_id", newOrgId, opts);
        setOrgId(newOrgId);
      }
      if (newOrgName) {
        Cookies.set("org_name", newOrgName, opts);
        setOrgName(newOrgName);
      }
      if (newUserId) {
        Cookies.set("user_id", newUserId, opts);
        setUserId(newUserId);
      }
      if (newFirstName) {
        Cookies.set("first_name", newFirstName, opts);
        setFirstName(newFirstName);
      }
      if (newLastName) {
        Cookies.set("last_name", newLastName, opts);
        setLastName(newLastName);
      }
      if (newEmail) {
        Cookies.set("email", newEmail, opts);
        setEmail(newEmail);
      }
    },
    []
  );

  // 🔑 Clear instantly
  const clearAuth = useCallback(() => {
    Cookies.remove("role", { path: "/" });
    Cookies.remove("access", { path: "/" });
    Cookies.remove("refresh", { path: "/" });
    Cookies.remove("org_id", { path: "/" });
    Cookies.remove("org_name", { path: "/" });
    Cookies.remove("user_id", { path: "/" });
    Cookies.remove("first_name", { path: "/" });
    Cookies.remove("last_name", { path: "/" });
    Cookies.remove("email", { path: "/" });
    queryClient.clear();
    setRole(undefined);
    setOrgId(undefined);
    setOrgName(undefined);
    setUserId(undefined);
    setFirstName(undefined);
    setLastName(undefined);
    setEmail(undefined);
  }, []);

  const value = useMemo(
    () => ({
      role,
      orgId,
      orgName,
      userId,
      firstName,
      lastName,
      email,
      updateAuth,
      clearAuth,
    }),
    [role, orgId, orgName, userId, firstName, lastName, email, updateAuth, clearAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
